import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card, Row, Col, Spinner, Alert, Badge, Button, ProgressBar, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import {
  FaUserGraduate,
  FaClipboardList,
  FaBookOpen,
  FaTasks,
  FaChartLine,
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaLeaf,
  FaBell,
  FaCalendarAlt,
  FaCarSide,
  FaFileInvoiceDollar,
  FaShoppingBag,
  FaSeedling,
} from "react-icons/fa";
import Swal from "sweetalert2";
import useApi from "../hooks/useApi";

const iconMap = {
  enrollment: <FaUserGraduate size={28} />,
  test: <FaClipboardList size={28} />,
  booking: <FaBookOpen size={28} />,
  progress: <FaChartLine size={28} />,
};

const actionLinkMap = {
  assign_school: "/dashboard/profile",
  pending_payment: "/product",
  pending_service: "/services",
};

const UserDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showTreeModal, setShowTreeModal] = useState(false);
  const [treeSubmitting, setTreeSubmitting] = useState(false);
  const [treeChoice, setTreeChoice] = useState("");
  const [hasShownCongrats, setHasShownCongrats] = useState(false);
  const [invoiceDownloading, setInvoiceDownloading] = useState(false);
  const hasLoadedRef = useRef(false);
  const { bearerAuth } = useApi();

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await bearerAuth("user/dashboard");
      setDashboard(res?.data?.data || {});
    } catch (_) {
      setError("Failed to load dashboard");
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [bearerAuth]);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    loadDashboard();
  }, [loadDashboard]);

  const data = dashboard || {};
  const userProfile = data.user_profile || {};
  const summaryCards = data.summary_cards || [];
  const displayName = userProfile.full_name || userProfile.name || userProfile.username || "User";
  const activeEnrollment = data.active_enrollment || null;
  const learningProgress = data.learning_progress || null;
  const testPerformance = data.test_performance || {};
  const productBookings = data.product_bookings || {};
  const serviceBookings = data.service_bookings || {};
  const classNotifications = data.class_notifications || [];
  const treeContribution = data.tree_contribution || {};
  const quickActions = data.quick_actions || { pending_actions: [] };
  const lastUpdated = data.last_updated || "";
  const completionPercentage = Number(learningProgress?.completion_percentage || 0);
  const completedSessions = Number(learningProgress?.completed_sessions || 0);
  const totalSessions = Number(learningProgress?.total_sessions || 0);
  const hasCompletedAllClasses =
    Boolean(activeEnrollment) && totalSessions > 0 && completionPercentage >= 100 && completedSessions >= totalSessions;
  const hasTreeContribution = Boolean(treeContribution.opted_in);
  const shouldShowCongratsPopup = hasCompletedAllClasses && !hasTreeContribution;

  useEffect(() => {
    if (!shouldShowCongratsPopup || !userProfile.id || hasShownCongrats) return;

    const storageKey = `dashboard-congrats-${userProfile.id}`;
    if (localStorage.getItem(storageKey) === "1") {
      setHasShownCongrats(true);
      return;
    }

    localStorage.setItem(storageKey, "1");
    setHasShownCongrats(true);

    Swal.fire({
      icon: "success",
      title: "Congratulations!",
      html: "You have completed your last class. Plant a tree first, then go to mock test to download your certificate.",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Plant a Tree",
      denyButtonText: "Go to Mock Test",
      cancelButtonText: "Close",
      reverseButtons: false,
      focusCancel: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setShowTreeModal(true);
      } else if (result.isDenied) {
        navigate("/dashboard/progress");
      }
    });
  }, [shouldShowCongratsPopup, hasShownCongrats, navigate, userProfile.id]);

  const openTreeModal = () => {
    setTreeChoice("");
    setShowTreeModal(true);
  };

  const handleTreeSelect = async (method) => {
    if (!userProfile?.id) {
      Swal.fire({
        icon: "error",
        title: "Missing user",
        text: "We could not find your user profile.",
      });
      return;
    }

    setTreeChoice(method);
    setTreeSubmitting(true);
    try {
      const response = await bearerAuth("opt-in-tree", "POST", {
        user_id: userProfile.id,
        planting_method: method,
      });

      if (response.data?.status) {
        setShowTreeModal(false);
        setDashboard((prev) => ({
          ...prev,
          tree_contribution: {
            ...(prev?.tree_contribution || {}),
            opted_in: true,
            planting_method: method,
          },
        }));

        Swal.fire({
          icon: "success",
          title: "Tree planting saved",
          text: "You can go over product and services page to explore there.",
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Explore Products",
          denyButtonText: "Explore Services",
          cancelButtonText: "Later",
          reverseButtons: true,
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/product");
          } else if (result.isDenied) {
            navigate("/services");
          }
        });
      } else {
        throw new Error(response.data?.message || "Failed to save tree preference");
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not save",
        text: err?.response?.data?.message || err?.message || "Failed to save tree preference.",
      });
    } finally {
      setTreeSubmitting(false);
    }
  };

  const handleInvoiceDownload = async () => {
    if (!activeEnrollment?.id) {
      Swal.fire({
        icon: "info",
        title: "Invoice unavailable",
        text: "We could not find an active enrollment to generate an invoice.",
      });
      return;
    }

    setInvoiceDownloading(true);
    try {
      const response = await bearerAuth(
        "user/course-invoice/download",
        "GET",
        { enrollment_id: activeEnrollment.id },
        { responseType: "blob" }
      );

      const blob = response?.data;
      if (!blob) {
        throw new Error("Invoice download failed.");
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `course_invoice_${activeEnrollment.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      Swal.fire({
        icon: "success",
        title: "Invoice downloaded",
        text: "Your course invoice has been downloaded successfully.",
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Download failed",
        text: err?.response?.data?.message || err?.message || "Unable to download invoice right now.",
      });
    } finally {
      setInvoiceDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="d-flex align-items-center justify-content-between">
        <span>{error}</span>
        <button className="btn btn-outline-danger btn-sm" onClick={loadDashboard}>
          Retry
        </button>
      </Alert>
    );
  }

  const enrollmentStatusLabel = (status) => {
    if (status === 1 || status === "pending") return "Pending";
    if (status === 2 || status === "accepted" || status === "in_progress") return "In Progress";
    if (status === 3 || status === "completed") return "Completed";
    if (status === 4 || status === "cancelled") return "Cancelled";
    return "Active";
  };

  const enrollmentStatusVariant = (status) => {
    if (status === 1 || status === "pending") return "warning";
    if (status === 2 || status === "accepted" || status === "in_progress") return "primary";
    if (status === 3 || status === "completed") return "success";
    if (status === 4 || status === "cancelled") return "danger";
    return "secondary";
  };

  const pendingActions = quickActions.pending_actions || [];
  const recentProgress = learningProgress?.recent_progress || [];
  const recentTests = testPerformance?.recent_tests || [];
  const recentProductBookings = productBookings?.recent_bookings || [];
  const recentServiceBookings = serviceBookings?.recent_bookings || [];

  const secondaryStats = [
    {
      title: "Mock Test Average",
      value: `${testPerformance.average_score ?? 0}`,
      helper: `${testPerformance.total_tests_taken ?? 0} tests taken`,
      icon: <FaClipboardList />,
      tone: "#0d6efd",
      soft: "rgba(13,110,253,0.12)",
    },
    {
      title: "Highest Test Score",
      value: `${testPerformance.highest_score ?? 0}`,
      helper:
        testPerformance.latest_score != null
          ? `Latest score: ${testPerformance.latest_score}`
          : "No attempts yet",
      icon: <FaChartLine />,
      tone: "#198754",
      soft: "rgba(25,135,84,0.12)",
    },
    {
      title: "Product Bookings",
      value: `${productBookings.total_bookings ?? 0}`,
      helper: `${productBookings.pending_bookings ?? 0} pending`,
      icon: <FaShoppingBag />,
      tone: "#fd7e14",
      soft: "rgba(253,126,20,0.12)",
    },
    {
      title: "Service Bookings",
      value: `${serviceBookings.total_bookings ?? 0}`,
      helper: `${serviceBookings.pending_bookings ?? 0} pending`,
      icon: <FaBookOpen />,
      tone: "#6f42c1",
      soft: "rgba(111,66,193,0.12)",
    },
  ];

  return (
    <div
    className="container-fluid px-3 px-lg-4 py-4"
    style={{
      minHeight: "calc(100vh - 70px)",
    }}
  >
      <style>{`
        .user-dashboard-shell {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .user-dashboard-hero {
          border: 0;
          border-radius: 26px;
          overflow: hidden;
          background:
            radial-gradient(circle at top right, rgba(255,255,255,0.18), transparent 28%),
            linear-gradient(135deg, #0f3d91 0%, #2767d8 55%, #53b7d2 100%);
          color: #fff;
          box-shadow: 0 24px 60px rgba(15, 61, 145, 0.18);
        }
        .user-dashboard-hero .card-body {
          padding: 1.5rem;
        }
        .hero-meta-chip {
          display: inline-flex;
          align-items: center;
          gap: .45rem;
          padding: .45rem .75rem;
          border-radius: 999px;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.14);
          font-size: .85rem;
          color: rgba(255,255,255,0.92);
        }
        .dashboard-stat-card {
          border: 0;
          border-radius: 22px;
          box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
          overflow: hidden;
          height: 100%;
        }
        .dashboard-stat-card .card-body {
          padding: 1.15rem;
        }
        .dashboard-stat-icon {
          width: 52px;
          height: 52px;
          border-radius: 16px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .dashboard-panel {
          border: 0;
          border-radius: 22px;
          box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
          height: 100%;
        }
        .dashboard-panel .card-body {
          padding: 1.25rem;
        }
        .dashboard-section-title {
          font-size: 1rem;
          font-weight: 700;
          color: #102a43;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: .6rem;
        }
        .metric-tile {
          border-radius: 18px;
          padding: 1rem;
          height: 100%;
          border: 1px solid rgba(15, 23, 42, 0.06);
          background: linear-gradient(180deg, #fff, #f8fbff);
        }
        .activity-row {
          display: flex;
          align-items: flex-start;
          gap: .75rem;
          padding: .75rem 0;
          border-bottom: 1px solid #edf2f7;
        }
        .activity-row:last-child {
          border-bottom: 0;
          padding-bottom: 0;
        }
        .activity-dot {
          width: 10px;
          height: 10px;
          margin-top: .35rem;
          border-radius: 50%;
          flex-shrink: 0;
          background: #0d6efd;
        }
        .pending-action-card {
          border-radius: 16px;
          padding: .9rem 1rem;
          border: 1px solid rgba(15, 23, 42, 0.06);
          background: #fff;
        }
        @media (max-width: 991px) {
          .user-dashboard-hero .card-body,
          .dashboard-panel .card-body {
            padding: 1rem;
          }
        }
      `}</style>

      <div className="user-dashboard-shell">
        <Card className="user-dashboard-hero">
          <Card.Body>
            <Row className="g-4 align-items-center">
              <Col lg={8}>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <span className="hero-meta-chip">
                    <FaUserGraduate />
                    {userProfile.email_verified ? "Verified Member" : "Verification Pending"}
                  </span>
                  <span className="hero-meta-chip">
                    <FaCalendarAlt />
                    Member since {userProfile.member_since || "—"}
                  </span>
                  <span className="hero-meta-chip">
                    <FaBell />
                    Updated {lastUpdated || "—"}
                  </span>
                </div>
                <h2 className="fw-bold mb-2">Welcome back, {displayName}</h2>
                <p className="mb-0" style={{ color: "rgba(255,255,255,0.88)" }}>
                  Track your training, review your next actions, and keep your enrollment progress moving without digging through multiple pages.
                </p>
              </Col>
              <Col lg={4}>
                <div className="bg-white bg-opacity-10 rounded-4 p-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small text-uppercase fw-semibold" style={{ letterSpacing: ".04em" }}>
                      Training Snapshot
                    </span>
                    <Badge bg={activeEnrollment ? "light" : "secondary"} text={activeEnrollment ? "dark" : "light"}>
                      {activeEnrollment ? "Active" : "Not Enrolled"}
                    </Badge>
                  </div>
                  <div className="fs-5 fw-semibold mb-1">
                    {activeEnrollment?.course_name || "No course selected yet"}
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.82)" }}>
                    {activeEnrollment?.school_name || "Choose a course and school to begin."}
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row className="g-4">
          {summaryCards.length ? (
            summaryCards.map((card, index) => (
              <Col xl={3} md={6} key={index}>
                <Card className="dashboard-stat-card">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="text-muted small fw-semibold text-uppercase mb-2" style={{ letterSpacing: ".04em" }}>
                          {card.title}
                        </div>
                        <div className="display-6 fw-bold mb-1">{card.value}</div>
                      </div>
                      <div className={`dashboard-stat-icon bg-${card.color} bg-opacity-10 text-${card.color}`}>
                        {iconMap[card.icon]}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))
          ) : (
            <Col>
              <Alert variant="info" className="mb-0">No summary data available.</Alert>
            </Col>
          )}
        </Row>

        <Row className="g-4">
          <Col xl={8}>
            <Card className="dashboard-panel">
              <Card.Body>
                <div className="dashboard-section-title">
                  <FaCarSide />
                  Enrollment Overview
                </div>
                {!activeEnrollment ? (
                  <Alert variant="info" className="mb-0">
                    No active enrollment found. Complete enrollment to unlock training, progress tracking, and school updates.
                  </Alert>
                ) : (
                  <Row className="g-3">
                    <Col md={7}>
                      <div className="metric-tile">
                        <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                          <div>
                            <div className="small text-uppercase text-muted fw-semibold mb-1">Current Course</div>
                            <div className="fs-5 fw-bold">{activeEnrollment.course_name}</div>
                            <div className="text-muted">{activeEnrollment.school_name || "School not assigned"}</div>
                          </div>
                          <Badge bg={enrollmentStatusVariant(activeEnrollment.status)}>
                            {enrollmentStatusLabel(activeEnrollment.status)}
                          </Badge>
                        </div>
                        <Row className="g-3">
                          <Col sm={6}>
                            <div className="small text-muted">Enrollment Code</div>
                            <div className="fw-semibold">{activeEnrollment.enrollment_code || "—"}</div>
                          </Col>
                          <Col sm={6}>
                            <div className="small text-muted">Time Slot</div>
                            <div className="fw-semibold">{activeEnrollment.time_slot || "Not scheduled"}</div>
                          </Col>
                          <Col sm={6}>
                            <div className="small text-muted">Expected Start</div>
                            <div className="fw-semibold">{activeEnrollment.expected_start_date || "—"}</div>
                          </Col>
                          <Col sm={6}>
                            <div className="small text-muted">School Contact</div>
                            <div className="fw-semibold">{activeEnrollment.school_contact || "—"}</div>
                          </Col>
                          <Col sm={6}>
                            <div className="small text-muted">Course Price</div>
                            <div className="fw-semibold">
                              {activeEnrollment.course_price !== null && activeEnrollment.course_price !== undefined
                                ? `₹${Number(activeEnrollment.course_price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
                                : "—"}
                            </div>
                          </Col>
                        </Row>
                        <div className="d-flex flex-wrap gap-2 mt-3">
                          <Button
                            variant="outline-primary"
                            className="fw-semibold"
                            onClick={handleInvoiceDownload}
                            disabled={invoiceDownloading}
                          >
                            <FaFileInvoiceDollar className="me-2" />
                            {invoiceDownloading ? "Downloading..." : "Download Invoice"}
                          </Button>
                        </div>
                      </div>
                    </Col>
                    <Col md={5}>
                      <div className="metric-tile h-100">
                        <div className="small text-uppercase text-muted fw-semibold mb-2">Learning Progress</div>
                        {learningProgress ? (
                          <>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="fw-semibold">Completion</span>
                              <span className="fw-bold">{learningProgress.completion_percentage}%</span>
                            </div>
                            <ProgressBar
                              now={Number(learningProgress.completion_percentage) || 0}
                              style={{ height: "10px", borderRadius: "999px" }}
                              className="mb-3"
                            />
                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">Completed</span>
                              <span className="fw-semibold">{learningProgress.completed_sessions}/{learningProgress.total_sessions}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">Pending</span>
                              <span className="fw-semibold">{learningProgress.pending_sessions}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Avg Class Rating</span>
                              <span className="fw-semibold">{learningProgress.average_rating ?? "N/A"}</span>
                            </div>
                          </>
                        ) : (
                          <div className="text-muted">No progress has been recorded yet.</div>
                        )}
                      </div>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="dashboard-panel">
              <Card.Body>
                <div className="dashboard-section-title">
                  <FaTasks />
                  Action Center
                </div>
                {pendingActions.length === 0 ? (
                  <div className="pending-action-card d-flex align-items-center gap-2">
                    <FaCheckCircle className="text-success" />
                    <span>No urgent actions. Your dashboard is up to date.</span>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {pendingActions.map((action, index) => (
                      <div key={index} className="pending-action-card">
                        <div className="d-flex justify-content-between align-items-start gap-3">
                          <div>
                            <div className={`small fw-bold text-uppercase mb-1 ${action.priority === "high" ? "text-danger" : "text-warning"}`}>
                              {action.priority} priority
                            </div>
                            <div>{action.message}</div>
                          </div>
                          <Button
                            as={Link}
                            to={actionLinkMap[action.type] || "/dashboard/profile"}
                            size="sm"
                            variant={action.priority === "high" ? "outline-danger" : "outline-warning"}
                          >
                            Open
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4">
                  <div className="small text-uppercase text-muted fw-semibold mb-2">Quick Links</div>
                  <div className="d-grid gap-2">
                    <Button as={Link} to="/dashboard/profile" variant="outline-primary">
                      Manage Profile
                    </Button>
                    <Button as={Link} to="/dashboard/schedule" variant="outline-secondary">
                      View Schedule
                    </Button>
                    <Button as={Link} to="/dashboard/progress" variant="outline-success">
                      Review Mock Tests
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4">
          <Col xl={6}>
            <Card className="dashboard-panel">
              <Card.Body>
                <div className="dashboard-section-title">
                  <FaClipboardList />
                  Performance Insights
                </div>
                <Row className="g-3 mb-3">
                  {secondaryStats.map((item, index) => (
                    <Col sm={6} key={index}>
                      <div className="metric-tile">
                        <div
                          className="d-inline-flex align-items-center justify-content-center rounded-3 mb-3"
                          style={{
                            width: 42,
                            height: 42,
                            color: item.tone,
                            background: item.soft,
                          }}
                        >
                          {item.icon}
                        </div>
                        <div className="small text-muted mb-1">{item.title}</div>
                        <div className="fs-4 fw-bold">{item.value}</div>
                        <div className="small text-muted">{item.helper}</div>
                      </div>
                    </Col>
                  ))}
                </Row>

                <div>
                  <div className="small text-uppercase text-muted fw-semibold mb-2">Recent Test Attempts</div>
                  {recentTests.length === 0 ? (
                    <div className="text-muted">No recent mock tests.</div>
                  ) : (
                    recentTests.map((test) => (
                      <div key={test.id} className="activity-row">
                        <div className="activity-dot"></div>
                        <div className="flex-grow-1">
                          <div className="fw-semibold">Score {test.score}</div>
                          <div className="small text-muted">{test.taken_on}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={6}>
            <Card className="dashboard-panel">
              <Card.Body>
                <div className="dashboard-section-title">
                  <FaClock />
                  Recent Activity
                </div>
                {recentProgress.length === 0 && recentProductBookings.length === 0 && recentServiceBookings.length === 0 ? (
                  <div className="text-muted">No recent activity available yet.</div>
                ) : (
                  <>
                    {recentProgress.slice(0, 3).map((entry, index) => (
                      <div key={`progress-${index}`} className="activity-row">
                        <div className="activity-dot" style={{ background: "#198754" }}></div>
                        <div className="flex-grow-1">
                          <div className="fw-semibold">
                            Session {entry.status === "completed" ? "completed" : entry.status}
                          </div>
                          <div className="small text-muted">
                            {entry.date || "—"}{entry.star ? ` | Rating ${entry.star}/5` : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                    {recentProductBookings.slice(0, 2).map((entry) => (
                      <div key={`product-${entry.id}`} className="activity-row">
                        <div className="activity-dot" style={{ background: "#fd7e14" }}></div>
                        <div className="flex-grow-1">
                          <div className="fw-semibold">{entry.product_title}</div>
                          <div className="small text-muted">
                            Product booking {entry.status_label} | {entry.booked_on}
                          </div>
                        </div>
                      </div>
                    ))}
                    {recentServiceBookings.slice(0, 2).map((entry) => (
                      <div key={`service-${entry.id}`} className="activity-row">
                        <div className="activity-dot" style={{ background: "#6f42c1" }}></div>
                        <div className="flex-grow-1">
                          <div className="fw-semibold">{entry.vendor_name}</div>
                          <div className="small text-muted">
                            Service booking {entry.status_label} | {entry.booked_on}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4">
          <Col>
            <Card className="dashboard-panel">
              <Card.Body>
                <div className="dashboard-section-title">
                  <FaBell />
                  Class Notifications
                </div>
                {classNotifications.length === 0 ? (
                  <div className="text-muted">No class notifications yet.</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {classNotifications.slice(0, 8).map((item, index) => (
                      <div
                        key={`class-notification-${index}`}
                        className="pending-action-card d-flex align-items-start justify-content-between gap-3"
                        style={{
                          borderLeft: `4px solid ${
                            item.severity === "success"
                              ? "#198754"
                              : item.severity === "danger"
                                ? "#dc3545"
                                : "#0d6efd"
                          }`,
                        }}
                      >
                        <div>
                          <div className="fw-semibold mb-1">{item.title || "Class update"}</div>
                          <div className="text-muted small">
                            {item.subtitle || "Your class status has been updated."}
                          </div>
                          {item.remarks ? (
                            <div className="small mt-1">
                              <span className="text-muted">Remarks:</span> {item.remarks}
                            </div>
                          ) : null}
                        </div>
                        <div className="text-end">
                          <Badge bg={item.severity || "info"} className="mb-2">
                            {item.status || "info"}
                          </Badge>
                          <div className="small text-muted">
                            {item.created_at ? new Date(item.created_at).toLocaleString() : item.date || "Just now"}
                          </div>
                          <Button
                            as={Link}
                            to={"/dashboard/notification"}
                            size="sm"
                            variant="outline-primary"
                            className="mt-2"
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4">
          <Col xl={8}>
            <Card className="dashboard-panel">
              <Card.Body>
                <div className="dashboard-section-title">
                  <FaBell />
                  Useful Snapshot
                </div>
                <Row className="g-3">
                  <Col md={4}>
                    <div className="metric-tile">
                      <div className="small text-muted mb-1">Pending Product Bookings</div>
                      <div className="fs-4 fw-bold">{productBookings.pending_bookings ?? 0}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="metric-tile">
                      <div className="small text-muted mb-1">Confirmed Service Bookings</div>
                      <div className="fs-4 fw-bold">{serviceBookings.confirmed_bookings ?? 0}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="metric-tile">
                      <div className="small text-muted mb-1">Tree Contribution</div>
                      <div className="fs-4 fw-bold d-flex align-items-center gap-2">
                        <FaLeaf className={treeContribution.opted_in ? "text-success" : "text-muted"} />
                        {treeContribution.opted_in ? "Active" : "Not Joined"}
                      </div>
                      <div className="small text-muted mb-3">
                        {treeContribution.planting_method || "No tree activity yet"}
                      </div>
                      {!treeContribution.opted_in && hasCompletedAllClasses && (
                        <Button variant="success" size="sm" onClick={openTreeModal} className="w-100">
                          <FaSeedling className="me-2" />
                          Plant a Tree
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="dashboard-panel">
              <Card.Body>
                <div className="dashboard-section-title">
                  <FaArrowRight />
                  Recommended Next Step
                </div>
                {!activeEnrollment ? (
                  <>
                    <div className="text-muted mb-3">
                      Start by completing your course enrollment to unlock the full student journey.
                    </div>
                    <Button as={Link} to="/enroll" variant="primary" className="w-100">
                      Enroll in Course
                    </Button>
                  </>
                ) : quickActions.can_take_test ? (
                  <>
                    <div className="text-muted mb-3">
                      Keep momentum by reviewing progress or taking your next mock test.
                    </div>
                    <div className="d-grid gap-2">
                      <Button as={Link} to="/dashboard/progress" variant="primary">
                        Go to Mock Tests
                      </Button>
                      <Button as={Link} to="/dashboard/schedule" variant="outline-primary">
                        Open Training Progress
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-muted">Your account is ready. Explore the dashboard sections for more details.</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {hasCompletedAllClasses && (
          <Row className="g-4">
            <Col>
              <Card className="dashboard-panel border-0" style={{ background: "linear-gradient(135deg, #0f3d91, #2775d1 55%, #1fa07a)" }}>
                <Card.Body className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 text-white">
                  <div>
                    <div className="dashboard-section-title text-white mb-2">
                      <FaCheckCircle />
                      Last Class Completed
                    </div>
                    <p className="mb-0" style={{ color: "rgba(255,255,255,0.9)" }}>
                      Congratulations, you are ready for the mock test to download your certificate.
                      You can also plant a tree to celebrate this milestone.
                    </p>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      variant="light"
                      className="fw-semibold"
                      onClick={openTreeModal}
                      disabled={treeContribution.opted_in}
                    >
                      Plant a Tree
                    </Button>
                    <Button as={Link} to="/dashboard/progress" variant="outline-light" className="fw-semibold">
                      Go to Mock Test
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <Modal show={showTreeModal} onHide={() => !treeSubmitting && setShowTreeModal(false)} centered>
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="w-100 text-center">
              <FaLeaf className="me-2 text-success" />
              Plant a Tree
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted text-center mb-4">
              Choose how you would like to contribute after completing your training.
            </p>
            <div className="row g-3">
              <div className="col-md-6">
                <Button
                  variant="outline-primary"
                  className="w-100 h-100 p-3 text-start"
                  onClick={() => handleTreeSelect("self")}
                  disabled={treeSubmitting}
                >
                  <div className="fw-bold mb-1">Plant by Self</div>
                  <div className="small text-muted">You will plant the tree yourself.</div>
                </Button>
              </div>
              <div className="col-md-6">
                <Button
                  variant="outline-success"
                  className="w-100 h-100 p-3 text-start"
                  onClick={() => handleTreeSelect("asysta")}
                  disabled={treeSubmitting}
                >
                  <div className="fw-bold mb-1">Plant by ASYSTA</div>
                  <div className="small text-muted">ASYSTA team will plant it on your behalf.</div>
                </Button>
              </div>
            </div>
            {treeSubmitting && (
              <div className="text-center mt-4">
                <Spinner animation="border" size="sm" className="me-2" />
                Saving your choice...
              </div>
            )}
            {treeChoice && !treeSubmitting && (
              <Alert variant="info" className="mt-3 mb-0">
                Selected: <strong>{treeChoice === "self" ? "Plant by Self" : "Plant by ASYSTA"}</strong>
              </Alert>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default UserDashboard;
